import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import * as Leaflet from 'leaflet';
import 'leaflet.markercluster';
import { filter } from 'rxjs';
import { DialogAnimationService } from 'src/app/angular-core/services/dialog-animation.service';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { BasicXpacInfo, ControllerTypeFilter } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { getMapDevicesAction } from 'src/app/store/actions/devices.actions';
import { selectMapDevices } from 'src/app/store/selectors/devices.selectors';
import { IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';
import { RemoteMaintenanceItemInfoComponent } from '../remote-maintenance/remote-maintenance-item-info/remote-maintenance-item-info.component';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

const CLUSTER_MARKER_SIZE: number = 16;
const STORE_MARKER_SIZE: number = 12;

interface FilterForm {
  enableClustering: FormControl<boolean>;
  includeCcaps: FormControl<boolean>;
}

@UntilDestroy()
@Component({
  selector: 'xpac-map-view',
  templateUrl: './map-view.component.html',
  styleUrl: './map-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapViewComponent implements OnInit {

  public options: Leaflet.MapOptions = {
    layers: [
      new Leaflet.TileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      } as Leaflet.TileLayerOptions),
    ],
    zoom: 1,
    center: new Leaflet.LatLng(0, 0)
  };
  public filterForm: FormGroup<FilterForm> = null;

  private devices: BasicXpacInfo[] = null;

  public get isLoading(): boolean {
    return !hasValue(this.devices);
  }

  constructor (
    private readonly formBuilder: FormBuilder,
    private readonly store: Store<IRemoteXPACPortalState>,
    private readonly dialog: DialogAnimationService,
    private readonly translate: TranslateService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {
  }

  public getLayers(): Leaflet.Layer[] {
    const disableClustering = this.filterForm?.getRawValue().enableClustering === false;
    let options: Leaflet.MarkerClusterGroupOptions = {
      iconCreateFunction: (cluster) => {
        return this.createClusterIcon(cluster);
      },
      chunkedLoading: true,
      polygonOptions: {
        color: 'var(--cp-theme-accent)'
      },
    };
    if (disableClustering) {
      options.maxClusterRadius = 0;
    }
    return [
      new Leaflet.MarkerClusterGroup(options).addLayers(this.getMarkers())
    ];
  }

  private createStoreIcon(device: BasicXpacInfo): Leaflet.Icon | Leaflet.DivIcon {
    return new Leaflet.DivIcon({
      iconSize: new Leaflet.Point(STORE_MARKER_SIZE, STORE_MARKER_SIZE),
      className: device.isConnected ? 'map-icon-online' : 'map-icon-offline',
      html: `<span></span>`
    });
  }

  private createClusterIcon(cluster: Leaflet.MarkerCluster): Leaflet.Icon | Leaflet.DivIcon {
    const scale = Math.max(1, Math.ceil(Math.log10(cluster.getChildCount() + 1)));
    return new Leaflet.DivIcon({
      iconSize: new Leaflet.Point(CLUSTER_MARKER_SIZE * scale, CLUSTER_MARKER_SIZE * scale),
      className: 'map-icon-cluster',
      html: `<span>${cluster.getChildCount()}</span>`
    });
  }

  private getMarkers(): Leaflet.Marker[] {
    if (!this.devices) {
      return [];
    }

    return this.devices.map(d => {
      const title = this.getDeviceTitle(d);
      const marker = new Leaflet.Marker(new Leaflet.LatLng(d.storeLocation.latitude, d.storeLocation.longitude), {
        title: title,
        icon: this.createStoreIcon(d),
      });
      marker.on('click', () => {
        // We come from outside Angular, so we need to call via ngZone, otherwise the dialog does not load properly
        this.ngZone.run(() => {
          marker.closePopup();
          this.dialog.open(RemoteMaintenanceItemInfoComponent, {
            panelClass: 'my-full-screen-dialog',
            data: { xpac: d },
            animation: { to: "left" }
          });
        });
      });
      return marker;
    });
  }

  private getDeviceTitle(device: BasicXpacInfo): string {
    let title: string = device.storeName;
    if (title.length === 0) {
      title = device.deviceId;
    }
    if (device.ccap) {
      title += ' (CCAP)';
    }
    return title;
  }

  public ngOnInit(): void {
    this.filterForm = this.formBuilder.group({
      enableClustering: <boolean>true,
      includeCcaps: <boolean>true,
    });
    this.filterForm.controls.includeCcaps.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.refreshDevices();
      });

    this.store.select(selectMapDevices)
      .pipe(
        filter(hasValue),
        untilDestroyed(this)
      )
      .subscribe((devices) => {
        this.devices = devices;
        this.cdr.markForCheck();

        // Some warning logging so it's easier for us to find misconfigured devices
        const unknownLocationDevices = devices.filter(d => d.storeLocation.latitude === 0 && d.storeLocation.longitude === 0);
        if (unknownLocationDevices.length > 0) {
          console.warn(`Found ${unknownLocationDevices.length} devices without a location!`, unknownLocationDevices);
        }
      });

    this.refreshDevices();
  }

  private refreshDevices(): void {
    this.store.dispatch(getMapDevicesAction({
      data: {
        type: this.filterForm.getRawValue().includeCcaps ? ControllerTypeFilter.All : ControllerTypeFilter.Xpac,
      }
    }));
  }
}
